import { BAND_COLORS } from "../bands";

// VDI — Vasculitis Damage Index.
// A checklist of organ-damage items grouped by system; each present item scores
// 1 point (damage is cumulative and irreversible — items are not un-scored).
// Total = number of items marked "Yes" (0–64).
// ⚠️ Only score damage present for ≥3 months and attributable to disease onset,
// not pre-existing co-morbidity. Verify item set clinically before use.
// ⚠️ This order is a persistence contract: field keys are positional (`d<section>_<item>`)
// and saved scores store them, so reordering or removing an item silently rehydrates
// old visits onto the wrong items. Only append.
const SECTIONS = [
    {
        name: "Musculoskeletal",
        items: [
            "Significant muscle atrophy or weakness",
            "Deforming/erosive arthritis",
            "Osteoporosis/vertebral collapse",
            "Avascular necrosis",
            "Osteomyelitis",
        ],
    },
    {
        name: "Skin/Mucous membranes",
        items: ["Alopecia", "Cutaneous ulcers", "Mouth ulcers"],
    },
    {
        name: "Ocular",
        items: [
            "Cataract",
            "Retinal change",
            "Optic atrophy",
            "Visual impairment/diplopia",
            "Blindness in one eye",
            "Blindness in second eye",
            "Orbital wall destruction",
        ],
    },
    {
        name: "ENT",
        items: [
            "Hearing loss",
            "Nasal blockage/chronic discharge/crusting",
            "Nasal bridge collapse/septal perforation",
            "Chronic sinusitis/radiological damage",
            "Subglottic stenosis (no surgery)",
            "Subglottic stenosis (with surgery)",
        ],
    },
    {
        name: "Pulmonary",
        items: [
            "Pulmonary hypertension",
            "Pulmonary fibrosis",
            "Pulmonary infarction",
            "Pleural fibrosis",
            "Chronic asthma",
            "Chronic breathlessness",
            "Impaired lung function",
        ],
    },
    {
        name: "Cardiovascular",
        items: [
            "Angina angioplasty",
            "Myocardial infarction",
            "Subsequent myocardial infarction",
            "Cardiomyopathy",
            "Valvular disease",
            "Pericarditis ≥ 3 months or pericardiectomy",
            "Diastolic BP ≥ 95 or requiring antihypertensives",
        ],
    },
    {
        name: "Peripheral vascular disease",
        items: [
            "Absent pulses in one limb",
            "Second episode of absent pulses in one limb",
            "Major vessel stenosis",
            "Claudication >3 months",
            "Minor tissue loss",
            "Major tissue loss",
            "Subsequent major tissue loss",
            "Complicated venous thrombosis",
        ],
    },
    {
        name: "Gastrointestinal",
        items: [
            "Gut infarction/resection",
            "Mesenteric insufficiency/pancreatitis",
            "Chronic peritonitis",
            "Esophageal stricture/surgery",
        ],
    },
    {
        name: "Renal",
        items: ["Estimated/measured GFR ≤ 50%", "Proteinuria ≥ 0.5g/24hr", "End stage renal disease"],
    },
    {
        name: "Neuropsychiatric",
        items: [
            "Cognitive impairment",
            "Major psychosis",
            "Seizures",
            "Cerebrovascular accident",
            "Second cerebrovascular accident",
            "Cranial nerve lesion",
            "Peripheral neuropathy",
            "Transverse myelitis",
        ],
    },
    {
        name: "Other",
        items: ["Gonadal failure", "Marrow failure", "Diabetes", "Chemical cystitis", "Malignancy", "Other"],
    },
];

const FIELDS = SECTIONS.flatMap((s, si) =>
    s.items.map((label, ii) => ({
        key: `d${si}_${ii}`,
        type: "toggle",
        section: s.name,
        label,
        default: 0,
    }))
);

export const vdi = {
    id: "vdi",
    name: "VDI",
    subtitle: "Vasculitis damage index",
    group: "Vasculitis",
    status: "ready",
    layout: "compact",
    options: [],
    fields: FIELDS,
    compute: (v) => FIELDS.reduce((sum, f) => sum + (Number(v[f.key]) || 0), 0),
    // No validated severity bands — report the raw damage-item count.
    interpret: (score) => ({
        label: score === 0 ? "No recorded damage" : `${score} damage item${score === 1 ? "" : "s"}`,
        color: score === 0 ? BAND_COLORS.remission : BAND_COLORS.moderate,
    }),
    about:
        "Vasculitis damage index (VDI) records organ damage that has occurred in patients since the onset of vasculitis. Patients often have co-morbidity before they develop vasculitis, which must not be scored. Record features of active disease using the Birmingham Vasculitis Activity Score (BVAS). A new patient should usually have a VDI score of zero, unless (a) they have had vasculitis for more than three months from onset of disease and (b) the damage has developed or become worse since the onset of vasculitis.",
    references: [
        "Exley AR, et al. Development and initial validation of the Vasculitis Damage Index (VDI). Arthritis Rheum. 1997.",
    ],
};
